import { useState, useEffect } from 'react';
import { Container, Row, Col, Table, Button, Tab, Nav } from 'react-bootstrap';
import { visitorService, commentService } from '../services/api';
import { getKnownBotName, countUniqueKnownIps } from '../utils/visitorTraffic';
import 'animate.css';
import TrackVisibility from 'react-on-screen';

export const AdminConsole = () => {
  const [visitors, setVisitors] = useState([]);
  const [comments, setComments] = useState([]);
  const [showBots, setShowBots] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('visitors');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [visitorsData, commentsData] = await Promise.all([
        visitorService.getVisitors(100),
        commentService.getComments(true)
      ]);
      
      setVisitors(visitorsData);
      setComments(commentsData);
    } catch (error) {
      console.error('Error loading admin data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApproveComment = async (commentId) => {
    try {
      await commentService.approveComment(commentId);
      // Reload comments
      const commentsData = await commentService.getComments(true);
      setComments(commentsData);
    } catch (error) {
      console.error('Error approving comment:', error);
      alert('Failed to approve comment');
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!window.confirm('Are you sure you want to delete this comment? This action cannot be undone.')) {
      return;
    }
    
    try {
      await commentService.deleteComment(commentId);
      // Reload comments
      const commentsData = await commentService.getComments(true);
      setComments(commentsData);
    } catch (error) {
      console.error('Error deleting comment:', error);
      alert('Failed to delete comment');
    }
  };

  const handleResetVisitors = async () => {
    if (!window.confirm('Are you sure you want to reset ALL visitor data? This will delete all visitor records and cannot be undone.')) {
      return;
    }
    
    try {
      await visitorService.resetVisitors();
      // Reload data
      await loadData();
      alert('Visitor data has been reset successfully');
    } catch (error) {
      console.error('Error resetting visitors:', error);
      alert('Failed to reset visitor data');
    }
  };

  const botCount = visitors.filter(visitor => getKnownBotName(visitor.userAgent)).length;
  const visibleVisitors = showBots
    ? visitors
    : visitors.filter(visitor => !getKnownBotName(visitor.userAgent));
  const uniqueIps = countUniqueKnownIps(visibleVisitors);

  const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <section className="admin-console">
        <Container>
          <div className="loading-text">Loading admin data...</div>
        </Container>
      </section>
    );
  }

  return (
    <section className="admin-console" id="admin">
      <Container>
        <Row>
          <Col lg={12}>
            <TrackVisibility>
              {({ isVisible }) =>
                <div className={isVisible ? "animate__animated animate__fadeIn" : ""}>
                  <div className="admin-header">
                    <h2>Admin Console</h2>
                    <Button onClick={loadData} className="refresh-btn">Refresh Data</Button>
                  </div>

                  {/* Statistics Cards */}
                  <Row className="admin-stats">
                    <Col md={4}>
                      <div className="stat-card">
                        <h3>{visibleVisitors.length}</h3>
                        <p>Loaded visits shown</p>
                        <small>{uniqueIps} unique known IP {uniqueIps === 1 ? 'address' : 'addresses'}</small>
                      </div>
                    </Col>
                    <Col md={4}>
                      <div className="stat-card">
                        <h3>{comments.filter(c => c.approved).length}</h3>
                        <p>Approved Comments</p>
                      </div>
                    </Col>
                    <Col md={4}>
                      <div className="stat-card pending">
                        <h3>{comments.filter(c => !c.approved).length}</h3>
                        <p>Pending Comments</p>
                      </div>
                    </Col>
                  </Row>

                  <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k)}>
                    <Nav variant="pills" className="admin-nav mb-4">
                      <Nav.Item>
                        <Nav.Link eventKey="visitors">Visitor Log</Nav.Link>
                      </Nav.Item>
                      <Nav.Item>
                        <Nav.Link eventKey="comments">Comments Management</Nav.Link>
                      </Nav.Item>
                    </Nav>

                    <Tab.Content>
                      <Tab.Pane eventKey="visitors">
                        <div className="admin-table-container">
                          <div className="table-header-with-action">
                            <h3>Recorded Visits</h3>
                            <Button 
                              variant="danger" 
                              size="sm"
                              onClick={handleResetVisitors}
                              className="reset-btn"
                            >
                              Reset All Visitors
                            </Button>
                          </div>
                          <div className="visitor-filter">
                            <label className="visitor-filter-toggle">
                              <input type="checkbox" checked={showBots} onChange={event => setShowBots(event.target.checked)} />
                              Show known bots and automated traffic
                            </label>
                            <p role="status">
                              Showing {visibleVisitors.length} of {visitors.length} loaded records.{' '}
                              {showBots ? `${botCount} identified as automated traffic.` : `${botCount} known bot or automated records hidden.`}
                            </p>
                            <small>Counts cover loaded records only. Detection uses user-agent names; unrecognized traffic may still be automated.</small>
                          </div>
                          <Table aria-label="Visitor log" striped bordered hover responsive className="admin-table">
                            <thead>
                              <tr>
                                <th>Date & Time</th>
                                <th>IP Address</th>
                                <th>Location</th>
                                <th>Page</th>
                                <th>User Agent</th>
                                <th>Referer</th>
                              </tr>
                            </thead>
                            <tbody>
                              {visibleVisitors.map((visitor, idx) => (
                                <tr key={visitor.visitId || idx}>
                                  <td>{formatDate(visitor.timestamp)}</td>
                                  <td className="ip-cell">{visitor.ipAddress || 'N/A'}</td>
                                  <td className="location-cell">
                                    {visitor.city && visitor.country ? (
                                      <>
                                        <div className="location-main">{visitor.city}, {visitor.region}</div>
                                        <div className="location-sub">{visitor.country}</div>
                                      </>
                                    ) : 'Unknown'}
                                  </td>
                                  <td>{visitor.page}</td>
                                  <td className="user-agent-cell" title={visitor.userAgent}>
                                    {getKnownBotName(visitor.userAgent) && <span className="visitor-bot-label">{getKnownBotName(visitor.userAgent)}</span>}
                                    {visitor.userAgent || 'Unknown'}
                                  </td>
                                  <td>{visitor.referer}</td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                          {visibleVisitors.length === 0 && (
                            <p className="no-data">{visitors.length > 0 ? 'All loaded visits are known bots or automated traffic. Enable “Show known bots and automated traffic” to view them.' : 'No visitor data available'}</p>
                          )}
                        </div>
                      </Tab.Pane>

                      <Tab.Pane eventKey="comments">
                        <div className="admin-table-container">
                          <h3>Comments Management</h3>
                          <Table striped bordered hover responsive className="admin-table">
                            <thead>
                              <tr>
                                <th>Date</th>
                                <th>Name</th>
                                <th>Email</th>
                                <th>Comment</th>
                                <th>Status</th>
                                <th>Action</th>
                              </tr>
                            </thead>
                            <tbody>
                              {comments.map((comment) => (
                                <tr key={comment.commentId} className={comment.approved ? 'approved' : 'pending'}>
                                  <td>{formatDate(comment.timestamp)}</td>
                                  <td>{comment.name}</td>
                                  <td>{comment.email || 'N/A'}</td>
                                  <td className="comment-text-cell">{comment.comment}</td>
                                  <td>
                                    <span className={`status-badge ${comment.approved ? 'approved' : 'pending'}`}>
                                      {comment.approved ? 'Approved' : 'Pending'}
                                    </span>
                                  </td>
                                  <td>
                                    <div className="admin-action-buttons">
                                      {!comment.approved && (
                                        <Button 
                                          size="sm" 
                                          variant="success"
                                          onClick={() => handleApproveComment(comment.commentId)}
                                          className="me-2"
                                        >
                                          Approve
                                        </Button>
                                      )}
                                      <Button 
                                        size="sm" 
                                        variant="danger"
                                        onClick={() => handleDeleteComment(comment.commentId)}
                                      >
                                        Delete
                                      </Button>
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                          {comments.length === 0 && (
                            <p className="no-data">No comments available</p>
                          )}
                        </div>
                      </Tab.Pane>
                    </Tab.Content>
                  </Tab.Container>
                </div>
              }
            </TrackVisibility>
          </Col>
        </Row>
      </Container>
    </section>
  );
};
